<template>
  <form>
    <div class="row mb-2">
      <div class="col-sm-12">

        <div class="row mt-2">
          <div class="col-sm-12">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`traderId`)">
              <TraderMultiselect :class="parentProps.class" :disabled="!!form.id" :multiple="false" :small="false" v-model="form.trader">
              </TraderMultiselect>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.priority") }}:</label>
          <div class="col-sm-8">
            <button type="button" class="btn w-100" :class="priorityClass(form.priority)" @click.prevent="togglePriority()">{{ form.priority }}</button>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.expire_at") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`expiredAt`)">
            <DatePicker :class="parentProps.class" v-model="form.expiredAt" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
              <template v-slot="{ inputValue, togglePopover, inputEvents }">
                <input
                  class="form-control"
                  :placeholder="helper.trans('labels.expire_at')"
                  :value="inputValue"
                  @focus="togglePopover"
                  @change="inputEvents.change"
                />
              </template>
            </DatePicker>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-sm-4"></div>
          <div class="col-sm-8">
            <button
              v-for="preset in quickPresets"
              :key="preset.id"
              @click.prevent="applyPreset(preset)"
              type="button"
              class="btn btn-sm btn-outline-secondary me-2 mb-2"
            >
              {{ presetLabel(preset) }}
            </button>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.traffic_group") }}:</label>
          <div class="col-sm-8">
            <TrafficGroupMultiselect :multiple="false" v-model="form.trafficGroup"></TrafficGroupMultiselect>
          </div>
        </div>

      </div>
    </div>

  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, onMounted, computed} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import Multiselect from "vue-multiselect";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import ManualPriorityFormDTO from "@/models/form/ManualPriorityFormDTO";
import TraderMultiselect from "@/components/TraderMultiselect.vue";
import {DatePicker} from "v-calendar";
import moment from 'moment-timezone';
import ManualPriorityCreateRequestDTO from "@/models/request/ManualPriorityCreateRequestDTO";
import ManualPriorityUpdateRequestDTO from "@/models/request/ManualPriorityUpdateRequestDTO";
import TrafficGroupMultiselect from "@/components/TrafficGroupMultiselect.vue";
import {dateHelper} from "@/service/DateHelper";

const api = new APIClient();
const notification = useNotification()

type ManualIncrementSource = 'minutes' | 'hour_increment' | 'day_increment' | 'month_increment' | null;

type ManualPriorityFormMeta = {
  expiredAtManual: string | null;
  manualIncrementSource: ManualIncrementSource;
  hourIncrementCount: number;
};

export default defineComponent({
  props: {
    role: String
  },
  expose: ['setForm', 'save', 'getForm'],
  components: {
    TrafficGroupMultiselect,
    DatePicker,
    Multiselect,
    FormInputWrapper,
    TraderMultiselect
  },
  setup(_, { expose }) {
    const store = useStore();
    const state = reactive({
      isTeamLeadsSelectLoading: false,
      errors: {} as ValidationErrorDTO[]|any,
      form: {
        id: null,
        trader: null,
        trafficGroup: null,
        priority: null,
        expiredAt: null,
        expiredAtManual: null,
        manualIncrementSource: null as ManualIncrementSource,
        hourIncrementCount: 0,
      } as ManualPriorityFormDTO & ManualPriorityFormMeta,
    });

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const save = () => {
      setErrors([]);
      const form = getForm();

      let promise;
      const expiredAt = form.expiredAtManual ?? form.expiredAt;
      if (form.id) {
        const data = {
          ...form,
          expiredAt: dateHelper.toUTC(expiredAt),
          trafficGroupId: form.trafficGroup?.id,
        } as ManualPriorityUpdateRequestDTO;

        promise = api.call.traderManualPriorities.update(form.id, data)
      } else {
        const data = {
          ...form,
          expiredAt: dateHelper.toUTC(expiredAt),
          traderId: form.trader?.id,
          trafficGroupId: form.trafficGroup?.id,
        } as ManualPriorityCreateRequestDTO;

        promise = api.call.traderManualPriorities.create(data)
      }

      promise
        .catch((e: any) => {
          if (e.response && e.request && e.request.status === 400 && e.response.data) {
            setErrors(e.response.data)
          } else {
            console.log(e);
          }

        })

      return promise
    }

    const hasErrors = (path: string) => {
      return state.errors[path]?.length > 0
    }

    const getForm = (): ManualPriorityFormDTO & ManualPriorityFormMeta => {
      return state.form
    }

    const priorityClass = (priority: null|string): any => {
      switch (priority) {
        case 'low':
          return {'btn-outline-secondary': true};
        case 'medium':
          return {'btn-outline-warning': true};
        case 'high':
          return {'btn-outline-danger': true};
        case 'unlimited':
          return {'btn-outline-dark': true};
      }

      return {}
    }

    const quickPresets = [
      { id: '5m', type: 'minutes', value: 5 },
      { id: '10m', type: 'minutes', value: 10 },
      { id: '30m', type: 'minutes', value: 30 },
      { id: '1h', type: 'hour_increment', value: 1 },
      { id: '1d', type: 'day_increment', value: 1 },
      { id: '1mo', type: 'month_increment', value: 1 },
    ] as const;

    type QuickPreset = typeof quickPresets[number];

    const applyPreset = (preset: QuickPreset) => {
      const timezone = dateHelper.getUserTimeZone();
      const now = moment().tz(timezone);

      if (preset.type === 'minutes') {
        const base = state.form.manualIncrementSource === 'minutes' && state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : (state.form.expiredAtManual ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone) : now);

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'minute') : base.add(preset.value, 'minute');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        state.form.manualIncrementSource = 'minutes';
        state.form.hourIncrementCount = 0;
        state.form.expiredAt = state.form.expiredAtManual;
        return;
      }

      if (preset.type === 'hour_increment') {
        const base = state.form.manualIncrementSource === 'hour_increment' && state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : (state.form.expiredAtManual ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone) : now);

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'hour') : base.add(preset.value, 'hour');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        state.form.manualIncrementSource = 'hour_increment';
        state.form.hourIncrementCount += preset.value;
        state.form.expiredAt = state.form.expiredAtManual;
        return;
      }

      if (preset.type === 'day_increment') {
        const base = state.form.manualIncrementSource === 'day_increment' && state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : (state.form.expiredAtManual ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone) : now);

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'day') : base.add(preset.value, 'day');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        state.form.manualIncrementSource = 'day_increment';
        state.form.hourIncrementCount = 0;
        state.form.expiredAt = state.form.expiredAtManual;
        return;
      }

      if (preset.type === 'month_increment') {
        const base = state.form.manualIncrementSource === 'month_increment' && state.form.expiredAtManual
          ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone)
          : (state.form.expiredAtManual ? moment.tz(state.form.expiredAtManual, 'YYYY-MM-DD HH:mm:ss', timezone) : now);

        const next = base.isBefore(now) ? now.clone().add(preset.value, 'month') : base.add(preset.value, 'month');
        state.form.expiredAtManual = next.format('YYYY-MM-DD HH:mm:ss');
        state.form.manualIncrementSource = 'month_increment';
        state.form.hourIncrementCount = 0;
        state.form.expiredAt = state.form.expiredAtManual;
        return;
      }
    }

    const presetLabel = (preset: QuickPreset) => {
      const key = (() => {
        switch (preset.type) {
          case 'minutes':
            return 'labels.minutes_period';
          case 'hour_increment':
            return 'labels.hours_period';
          case 'day_increment':
            return 'labels.days_period';
          case 'month_increment':
            return 'labels.months_period';
          default:
            return null;
        }
      })();

      if (!key) {
        return '';
      }

      const translated = helper.trans(key, {
        minute: preset.value,
        hour: preset.value,
        day: preset.value,
        month: preset.value
      });

      return `+ ${translated}`;
    }

    const togglePriority = () => {
      const priorities = ['low', 'medium', 'high', 'unlimited'];
      const index = state.form.priority ? priorities.indexOf(state.form.priority) : 0;
      let newValue = priorities[index + 1];

      state.form.priority = newValue ?? priorities[0]
    }

    const setForm = (form: ManualPriorityFormDTO) => {
      state.errors = {}
      state.form = {
        ...form,
        expiredAtManual: form.expiredAt,
        manualIncrementSource: null,
        hourIncrementCount: 0,
      }
    }

    onMounted(async () => {
    })

    expose({
      getForm,
      setForm,
      save,
    })

    return {
      ...toRefs(state),
      getForm,
      setForm,
      store,
      helper,
      numberFormatter,
      setErrors,
      getErrors,
      hasErrors,
      save,
      togglePriority,
      priorityClass,
      quickPresets,
      applyPreset,
      presetLabel
    }
  }
})
</script>
